// useNetworkHealth.ts — Fetches IC mainnet network-level health metrics.
//
// Surfaces two IC Dashboard public REST endpoints:
//   - average-cycle-burn-rate across all subnets
//   - ICP/XDR conversion rate (current exchange rate)
//
// Mainnet-only: when VITE_DFX_NETWORK !== 'ic', returns a placeholder state.
// No auth required — these are public, unauthenticated REST APIs.
// Polls every 60s matching useCanisterHealth.ts cadence.
//
// ic-dashboard skill citation:
//   /api/v1/average-cycle-burn-rate  — average burn across all subnets
//   /api/v3/icp-xdr-conversion-rates — current XDR rate (most recent entry)
//
// Anti-drift: no @icp-sdk/* imports. No dfx CLI calls. Pure browser fetch().

import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;

// IC Dashboard public REST base — no API key required
const IC_METRICS_BASE = 'https://ic-api.internetcomputer.org';

// Whether we're running against mainnet
const IS_MAINNET = import.meta.env.VITE_DFX_NETWORK === 'ic';

export interface NetworkHealthData {
  // Average cycles burned per second across all IC subnets (raw bigint)
  avgCycleBurnRatePerSecond: bigint | null;
  // Human-friendly display string: "12.4T cycles/s"
  avgCycleBurnRateDisplay: string;
  // XDR per ICP (1 ICP = N XDR), 4 decimal places
  icpPerXdrDisplay: string;
  // Raw ratio — 1 ICP expressed in 10^4 XDR units (as returned by API)
  xdrPerIcp: number | null;
}

export interface UseNetworkHealthReturn {
  data: NetworkHealthData | null;
  isMainnet: boolean;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: Date | null;
  refresh: () => void;
}

function formatCycleBurnRate(ratePerSecond: number): string {
  // Convert cycles/s to trillions/day for readability
  const perDay = ratePerSecond * 86_400;
  const trillions = perDay / 1e12;
  if (trillions >= 1000) {
    return `${(trillions / 1000).toFixed(1)}P cycles/day`;
  }
  return `${trillions.toFixed(1)}T cycles/day`;
}

async function fetchBurnRate(): Promise<bigint | null> {
  // 2026-05-20: IC Foundation moved this endpoint. Old v1 path returns 404.
  // New shape: { cycle_burn_rate: [[timestamp_seconds, value_as_string], ...] }
  // We pull the most recent tuple (last entry in the time-series).
  const res = await fetch(`${IC_METRICS_BASE}/api/v3/metrics/cycle-burn-rate`, {
    signal: AbortSignal.timeout(10_000),
  });
  if (!res.ok) return null;
  const body = await res.json() as { cycle_burn_rate?: Array<[number, string]> };
  const entries = body.cycle_burn_rate;
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const latest = entries[entries.length - 1];
  if (!latest || typeof latest[1] !== 'string') return null;
  const value = Number(latest[1]);
  if (!Number.isFinite(value)) return null;
  // Store as bigint (rounded integer cycles/s)
  return BigInt(Math.round(value));
}

async function fetchXdrRate(): Promise<number | null> {
  // Returns current XDR/ICP conversion rate.
  // Response shape: { data: Array<{ xdr_permyriad_per_icp: number, timestamp_seconds: number }> }
  // xdr_permyriad_per_icp: 1 XDR = 10_000 permyriad, so rate = value / 10_000 XDR/ICP
  const res = await fetch(`${IC_METRICS_BASE}/api/v3/icp-xdr-conversion-rates`, {
    signal: AbortSignal.timeout(10_000),
  });
  if (!res.ok) return null;
  const body = await res.json() as {
    data?: Array<{ xdr_permyriad_per_icp?: number }>;
  };
  const entries = body.data;
  if (!Array.isArray(entries) || entries.length === 0) return null;
  // Most recent entry is last in array
  const latest = entries[entries.length - 1];
  if (typeof latest.xdr_permyriad_per_icp !== 'number') return null;
  return latest.xdr_permyriad_per_icp;
}

export function useNetworkHealth(): UseNetworkHealthReturn {
  const [data, setData] = useState<NetworkHealthData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchAll = useCallback(async () => {
    // Guard: only fetch real data on mainnet
    if (!IS_MAINNET) return;

    setIsLoading(true);
    setError(null);

    try {
      const [burnRate, xdrPermyriad] = await Promise.all([
        fetchBurnRate(),
        fetchXdrRate(),
      ]);

      if (!isMountedRef.current) return;

      const burnRateNum = burnRate !== null ? Number(burnRate) : null;

      setData({
        avgCycleBurnRatePerSecond: burnRate,
        avgCycleBurnRateDisplay: burnRateNum !== null
          ? formatCycleBurnRate(burnRateNum)
          : '—',
        icpPerXdrDisplay: xdrPermyriad !== null
          ? (xdrPermyriad / 10_000).toFixed(4)
          : '—',
        xdrPerIcp: xdrPermyriad,
      });
      setLastFetchedAt(new Date());
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch network health.';
      // Non-fatal: IC API may be briefly unavailable; surface error without clearing stale data
      setError(message);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!IS_MAINNET) return;

    isMountedRef.current = true;
    fetchAll();

    timerRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetchAll]);

  return { data, isMainnet: IS_MAINNET, isLoading, error, lastFetchedAt, refresh };
}
